import { useMutation } from "@tanstack/react-query";
import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouteContext,
} from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import type { ReactNode, JSX } from "react";

import type { DocumentType } from "@repo/graphql-types";
import { graphql } from "@repo/graphql-types/gql";
import {
  AppointmentTypesPageQueryDocument,
  FirstScheduleSuccessQueryDocument,
  PatientPreferencesPageQueryDocument,
  SchedulesPageQueryDocument,
  ServicePreferencesPageQueryDocument,
} from "@repo/graphql-types/graphql";

import { FirstScheduleFooter } from "@/components/onboarding/first-schedule-footer";
import { PatientAppointmentTypePreview } from "@/components/onboarding/patient-appointment-type-preview";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import { Page, PageTitle } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { FullPageSpinner } from "@/components/ui/loading/full-page-spinner";
import { useReturnToFirstScheduleStop } from "@/hooks/use-first-schedule-history";
import {
  useGraphQL,
  useGraphQLMutationWithErrorHandler,
  useInvalidateQuery,
  useResetQuery,
} from "@/hooks/use-graphql";
import {
  type FirstScheduleForm,
  firstScheduleAtom,
} from "@/lib/atoms/first-schedule-atom";
import { AppointmentTypeCategoryEnum } from "@/lib/enums/appointment-type-category-enum";
import { AppointmentTypeModalityEnum } from "@/lib/enums/appointment-type-modality-enum";
import { FollowUpPolicyEnum } from "@/lib/enums/follow-up-policy-enum";
import type { FollowUpDraft } from "@/lib/form-schemas/first-schedule-appointment-types-schema";
import { formatCurrency } from "@/lib/helpers/currency";
import {
  FIRST_SCHEDULE_STEPS,
  FOLLOW_UP_APPOINTMENT_TYPE_NAME,
  getFirstScheduleProgress,
  TELECONSULTATION_APPOINTMENT_TYPE_NAME,
} from "@/lib/helpers/first-schedule-flow";
import { requireCompleteAppointmentTypesDraft } from "@/lib/helpers/first-schedule-guards";
import { buildScheduleAppointmentTypesPatch } from "@/lib/helpers/schedule-appointment-types-patch";

const FirstSchedulePreviewEditAppointmentTypeMutation = graphql(/* GraphQL */ `
  mutation FirstSchedulePreviewEditAppointmentTypeMutation(
    $input: LivanceApiAlteraUsuarioCompromissoInput!
  ) {
    LivanceApiAlteraUsuarioCompromissoPatch(arg1: $input) {
      codUsuarioCompromisso
    }
  }
`);

const FirstSchedulePreviewUpdateScheduleMutation = graphql(/* GraphQL */ `
  mutation FirstSchedulePreviewUpdateScheduleMutation(
    $input: LivanceApiPatchUsuarioAgendaInput!
  ) {
    LivanceApiPatchUsuarioAgenda(arg1: $input)
  }
`);

const FirstSchedulePreviewUpdateFollowUpPolicyMutation = graphql(/* GraphQL */ `
  mutation FirstSchedulePreviewUpdateFollowUpPolicyMutation(
    $followUpPolicy: Int
    $daysToFreeFollowUp: Int
    $arriveLatePolicy: Int!
    $minimumAge: Int
    $maximumAge: Int
    $appointmentRules: String
    $sendConfirmationEmail: Boolean!
  ) {
    alterarDadosDeAtendimento(
      input: {
        codTipoPoliticaRetorno: $followUpPolicy
        retornoGratuitoEmDias: $daysToFreeFollowUp
        toleranciaAtrasoEmMinutos: $arriveLatePolicy
        idadeMinimaPaciente: $minimumAge
        idadeMaximaPaciente: $maximumAge
        orientacoesPacientes: $appointmentRules
        confirmarConsulta: $sendConfirmationEmail
      }
    ) {
      errors {
        ... on ValidationError {
          __typename
          message
        }
      }
    }
  }
`);

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const FirstSchedulePreviewQuery = graphql(/* GraphQL */ `
  query FirstSchedulePreviewQuery(
    $codAgendas: [Int!]!
    $codUsuario: Int!
    $followUpName: String!
    $teleconsultationName: String!
    $manualCategory: Int!
    $inPersonModality: Int!
    $teleconsultationModality: Int!
  ) {
    usuariosAgendas(
      where: { codUsuarioAgenda: { _in: $codAgendas } }
      order_by: { codUsuarioAgenda: asc }
    ) {
      codUsuarioAgenda
      dataInicio
      dataFim
      inicio
      fim
      codTipoAgendaRepeticao
      scheduleUnits: tbUsuariosAgendasUnidades(where: { ativo: { _eq: true } }) {
        codUnidade
      }
      scheduleDaysOfWeek: tbUsuariosAgendasDiasSemanas(where: { ativo: { _eq: true } }) {
        diaSemana
      }
      appointmentTypes: tbUsuariosAgendasCompromissos(
        where: { ativo: { _eq: true }, UsuarioCompromisso: { ativo: { _eq: true } } }
      ) {
        codUsuarioCompromisso
      }
    }
    followUpAppointmentTypes: usuariosCompromissos(
      where: {
        codUsuario: { _eq: $codUsuario }
        nome: { _eq: $followUpName }
        ativo: { _eq: true }
        categoria: { _eq: $manualCategory }
        codUsuarioCompromissoModalidade: { _eq: $inPersonModality }
      }
      order_by: { codUsuarioCompromisso: desc }
      limit: 1
    ) {
      codUsuarioCompromisso
    }
    teleconsultationAppointmentTypes: usuariosCompromissos(
      where: {
        codUsuario: { _eq: $codUsuario }
        nome: { _eq: $teleconsultationName }
        ativo: { _eq: true }
        categoria: { _eq: $manualCategory }
        codUsuarioCompromissoModalidade: { _eq: $teleconsultationModality }
      }
      order_by: { codUsuarioCompromisso: desc }
      limit: 1
    ) {
      codUsuarioCompromisso
      duracao
      valor
    }
    usuarios {
      professionalData: UsuariosProfissionai {
        confirmarConsulta
        idadeMaximaPaciente
        idadeMinimaPaciente
        orientacoes
        appointmentPreferences: configuracaoAtendimentos {
          toleranciaAtrasoEmMinutos
        }
      }
    }
  }
`);

const FIRST_APPOINTMENT_PREVIEW_NAME = "Consulta";
const FOLLOW_UP_PREVIEW_NAME = "Retorno";
const TELECONSULTATION_PREVIEW_NAME = "Teleconsulta";

const toPatientPriceLabel = (price: number): string =>
  price > 0 ? formatCurrency(price) : "Valor não informado";

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const getFollowUpPriceLabel = (followUp: FollowUpDraft): string => {
  switch (followUp.policy) {
    case FollowUpPolicyEnum.FreeFollowUp:
      return `Gratuito em até ${followUp.daysToFreeFollowUp} dias`;
    case FollowUpPolicyEnum.CaseByCase:
      return `Gratuito em até ${followUp.daysToFreeFollowUp} dias, a critério do profissional`;
    default:
      return toPatientPriceLabel(followUp.price);
  }
};

type PreviewData = DocumentType<typeof FirstSchedulePreviewQuery>;

const findFirstAppointmentType = (data: PreviewData) => {
  const followUpTypeId = data.followUpAppointmentTypes[0]?.codUsuarioCompromisso;
  return data.usuariosAgendas[0]?.appointmentTypes.find(
    (item) => item.codUsuarioCompromisso !== followUpTypeId,
  );
};

const useFirstSchedulePreviewData = (codAgendas: number[]) => {
  const { user } = useRouteContext({ from: "__root__" });

  return useGraphQL(
    FirstSchedulePreviewQuery,
    {
      codAgendas,
      codUsuario: user.codUsuario,
      followUpName: FOLLOW_UP_APPOINTMENT_TYPE_NAME,
      teleconsultationName: TELECONSULTATION_APPOINTMENT_TYPE_NAME,
      manualCategory: AppointmentTypeCategoryEnum.Manual,
      inPersonModality: AppointmentTypeModalityEnum.Presencial,
      teleconsultationModality: AppointmentTypeModalityEnum.Teleconsulta,
    },
    { enabled: codAgendas.length > 0 },
  );
};

interface SaveAppointmentTypesVariables {
  form: FirstScheduleForm;
  data: PreviewData;
}

const useSaveAppointmentTypes = () => {
  const { mutateAsync: editAppointmentType } = useGraphQLMutationWithErrorHandler(
    FirstSchedulePreviewEditAppointmentTypeMutation,
  );
  const { mutateAsync: updateSchedule } = useGraphQLMutationWithErrorHandler(
    FirstSchedulePreviewUpdateScheduleMutation,
  );
  const { mutateAsync: updatePatientPreferences } = useGraphQLMutationWithErrorHandler(
    FirstSchedulePreviewUpdateFollowUpPolicyMutation,
  );

  const resetSchedules = useResetQuery(SchedulesPageQueryDocument);
  const resetAppointmentTypes = useResetQuery(AppointmentTypesPageQueryDocument);
  const resetSuccess = useResetQuery(FirstScheduleSuccessQueryDocument);
  const invalidatePatientPreferences = useInvalidateQuery(
    PatientPreferencesPageQueryDocument,
  );
  const invalidateServicePreferences = useInvalidateQuery(
    ServicePreferencesPageQueryDocument,
  );

  const save = async ({ form, data }: SaveAppointmentTypesVariables): Promise<void> => {
    const { firstAppointment, followUp } = form;
    if (!firstAppointment || !followUp) return;

    const firstAppointmentTypeId = findFirstAppointmentType(data)?.codUsuarioCompromisso;
    const followUpTypeId = data.followUpAppointmentTypes[0]?.codUsuarioCompromisso;
    const professionalData = data.usuarios[0]?.professionalData;

    if (firstAppointmentTypeId) {
      await editAppointmentType({
        input: {
          codUsuarioCompromisso: firstAppointmentTypeId,
          jsonPatchDocument: [
            { op: "replace", path: "/valor", value: firstAppointment.price },
            { op: "replace", path: "/duracao", value: firstAppointment.duration },
          ],
        },
      });
    }

    if (followUpTypeId) {
      await editAppointmentType({
        input: {
          codUsuarioCompromisso: followUpTypeId,
          jsonPatchDocument: [
            { op: "replace", path: "/valor", value: followUp.price },
            { op: "replace", path: "/duracao", value: followUp.duration },
          ],
        },
      });
    }

    await updatePatientPreferences({
      followUpPolicy: followUp.policy,
      daysToFreeFollowUp: followUp.daysToFreeFollowUp ?? undefined,
      arriveLatePolicy:
        professionalData?.appointmentPreferences[0]?.toleranciaAtrasoEmMinutos ?? -1,
      minimumAge: professionalData?.idadeMinimaPaciente ?? undefined,
      maximumAge: professionalData?.idadeMaximaPaciente ?? undefined,
      appointmentRules: professionalData?.orientacoes ?? undefined,
      sendConfirmationEmail: professionalData?.confirmarConsulta ?? false,
    });

    // TODO(back-end): criarPrimeiraAgendaUsuario deve manter os tipos vinculados ao recriar as agendas.
    if (followUpTypeId) {
      await Promise.all(
        data.usuariosAgendas
          .filter((schedule) =>
            schedule.appointmentTypes.every(
              (item) => item.codUsuarioCompromisso !== followUpTypeId,
            ),
          )
          .map((schedule) =>
            updateSchedule({
              input: {
                codUsuarioAgenda: schedule.codUsuarioAgenda,
                jsonPatchDocument: buildScheduleAppointmentTypesPatch(schedule, [
                  ...schedule.appointmentTypes.map((item) => item.codUsuarioCompromisso),
                  followUpTypeId,
                ]),
              },
            }),
          ),
      );
    }

    resetSchedules();
    resetAppointmentTypes();
    resetSuccess();
    invalidatePatientPreferences();
    invalidateServicePreferences();
  };

  return useMutation({ mutationFn: save });
};

const FirstSchedulePreviewPage = (): JSX.Element | null => {
  const navigate = useNavigate();
  const form = useAtomValue(firstScheduleAtom);
  const returnToStop = useReturnToFirstScheduleStop();

  const { data, isPending } = useFirstSchedulePreviewData(form.codAgendas);
  const { mutate: saveAppointmentTypes, isPending: isSaving } = useSaveAppointmentTypes();

  const { firstAppointment, followUp } = form;
  if (!firstAppointment || !followUp) return null;

  if (isPending || !data) {
    return (
      <PreviewLayout>
        <FullPageSpinner />
      </PreviewLayout>
    );
  }

  const teleconsultationType = data.teleconsultationAppointmentTypes[0];

  const handleConfirm = (): void => {
    saveAppointmentTypes(
      { form, data },
      {
        onSuccess: () => {
          void navigate({
            to: "/onboarding/first-schedule/success",
            search: { appointmentTypes: "saved" },
          });
        },
      },
    );
  };

  return (
    <PreviewLayout>
      <div className="flex flex-col gap-4">
        <PageTitle>
          É assim que seus pacientes vão ver as opções de atendimento da sua agenda
        </PageTitle>

        <div className="flex flex-col gap-3">
          <PatientAppointmentTypePreview
            name={FIRST_APPOINTMENT_PREVIEW_NAME}
            duration={firstAppointment.duration}
            priceLabel={toPatientPriceLabel(firstAppointment.price)}
            onChange={() => {
              returnToStop("firstAppointment", () => {
                void navigate({
                  to: "/onboarding/first-schedule/first-appointment",
                  replace: true,
                });
              });
            }}
          />
          <PatientAppointmentTypePreview
            name={FOLLOW_UP_PREVIEW_NAME}
            duration={followUp.duration}
            priceLabel={getFollowUpPriceLabel(followUp)}
            onChange={() => {
              returnToStop("followUp", () => {
                void navigate({
                  to: "/onboarding/first-schedule/follow-up",
                  replace: true,
                });
              });
            }}
          />
          {teleconsultationType ? (
            <PatientAppointmentTypePreview
              name={TELECONSULTATION_PREVIEW_NAME}
              duration={teleconsultationType.duracao}
              priceLabel={toPatientPriceLabel(teleconsultationType.valor)}
              onChange={() => {
                void navigate({
                  to: "/settings/schedule-options/appointment-types/$appointmentTypeId",
                  params: {
                    appointmentTypeId: teleconsultationType.codUsuarioCompromisso,
                  },
                });
              }}
            />
          ) : null}
        </div>
      </div>

      <FirstScheduleFooter
        submitLabel="Está tudo certo"
        onSubmit={handleConfirm}
        disabled={isSaving}
        loading={isSaving}
      />
    </PreviewLayout>
  );
};

const PreviewLayout = ({ children }: { children: ReactNode }): JSX.Element => {
  const { flags } = useRouteContext({ from: "__root__" });

  return (
    <RoundedLayout>
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.BackButton />
          <FlowHeader.HelpButton />
        </FlowHeader.Actions>
        <FlowHeader.ProgressBar
          progress={getFirstScheduleProgress(
            FIRST_SCHEDULE_STEPS.preview,
            flags["exibe-configuracao-tipos-de-atendimento-na-primeira-agenda"],
          )}
        />
      </FlowHeader>

      <Page
        className="flex flex-col justify-between pb-0 px-6"
        showCustomerIoContainer={false}
      >
        {children}
      </Page>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/onboarding/first-schedule/preview")({
  beforeLoad: ({ context }) => {
    if (!context.flags["exibe-configuracao-tipos-de-atendimento-na-primeira-agenda"]) {
      redirect({ to: "/onboarding/first-schedule/success", throw: true });
    }
    requireCompleteAppointmentTypesDraft();
  },
  component: FirstSchedulePreviewPage,
});
